import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  CreateProjectBody,
  CreateProjectData,
  ServerApiError,
  serverApi,
  ServerApiConfig,
} from 'api/server';
import { ReduxState } from 'store';

import {
  MergeRequestState,
  getInitialRequestState,
  getFetchedRequestState,
  getFetchingRequestState,
} from '../serverUtils';

export interface CreateProjectState
  extends MergeRequestState<CreateProjectData, ServerApiError> {}

const selector = {
  state: (state: ReduxState) => state.server.createProject,
  isFetching: (state: ReduxState) => state.server.createProject.isFetching,
  data: (state: ReduxState) => state.server.createProject.data,
  error: (state: ReduxState) => state.server.createProject.error,
};

const initialState: CreateProjectState = {
  ...getInitialRequestState(),
  error: null,
  data: null,
};

const SLICE_NAME = '@server/createProject';

const requestThunk = createAsyncThunk(
  `${SLICE_NAME}/request`,
  (
    { body, config }: { body: CreateProjectBody; config?: ServerApiConfig },
    { rejectWithValue },
  ) => serverApi.createProject(body, config).catch(rejectWithValue),
  {
    condition: (payload, { getState }) =>
      !selector.isFetching(getState() as ReduxState),
  },
);

const { actions, reducer } = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(requestThunk.pending, (state) => {
      Object.assign(state, getFetchingRequestState());
    });
    builder.addCase(requestThunk.fulfilled, (state, action) => {
      Object.assign(state, getFetchedRequestState());
      state.data = action.payload;
      state.error = null;
    });
    builder.addCase(requestThunk.rejected, (state, action) => {
      Object.assign(state, initialState);
      state.error = action.payload as ServerApiError;
    });
  },
});

interface ServerCreateProject {
  action: typeof actions;
  thunk: {
    request: typeof requestThunk;
  };
  reducer: typeof reducer;
  selector: typeof selector;
}

export const createProject: ServerCreateProject = {
  action: actions,
  thunk: {
    request: requestThunk,
  },
  reducer,
  selector,
};
